/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button, ErrorText, Field, Input, PageHeader } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { applyBranding, DEFAULT_BRANDING, palette, useBranding, type Branding } from '@/lib/branding';

/** M27 Фирменный стиль: название, цвет и логотип в CRM, кабинете клиента и приложениях */
export function BrandingPage() {
  const qc = useQueryClient();
  const { hasModule } = useAuth();
  const cur = useBranding();
  const [b, setB] = useState<Branding>(cur);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setB(cur), [cur]);
  const save = useMutation({ mutationFn: () => api.put<Branding>('/admin/branding', b), onSuccess: (r) => { applyBranding(r); qc.setQueryData(['branding'], r); } });
  const onLogo = (f?: File) => {
    setErr(null);
    if (!f) return;
    if (f.size > 200_000) { setErr('Логотип больше 200 КБ: уменьшите изображение'); return; }
    const r = new FileReader(); r.onload = () => setB({ ...b, logo: String(r.result) }); r.readAsDataURL(f);
  };
  if (!hasModule('M27')) return <><PageHeader title="Фирменный стиль" /><div className="card p-4 text-sm text-slate-600">Модуль M27 «White Label» не входит в лицензию.</div></>;
  const pal = palette(b.color);
  return (
    <>
      <PageHeader title="Фирменный стиль" subtitle="Название, цвет и логотип вашей организации в интерфейсе, кабинете клиента и приложениях" />
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="card space-y-3 p-4">
          <Field label="Название в интерфейсе"><Input value={b.name} onChange={(e) => setB({ ...b, name: e.target.value })} /></Field>
          <Field label="Короткое название" hint="Для иконки приложения на телефоне"><Input value={b.shortName} onChange={(e) => setB({ ...b, shortName: e.target.value })} /></Field>
          <Field label="Заголовок кабинета клиента"><Input value={b.portalTitle} onChange={(e) => setB({ ...b, portalTitle: e.target.value })} /></Field>
          <Field label="Название приложения для клиентов"><Input value={b.appName} onChange={(e) => setB({ ...b, appName: e.target.value })} /></Field>
          <Field label="Фирменный цвет"><div className="flex items-center gap-2"><input type="color" value={b.color} onChange={(e) => setB({ ...b, color: e.target.value })} className="h-9 w-14 rounded border border-slate-300" /><Input className="w-32" value={b.color} onChange={(e) => setB({ ...b, color: e.target.value })} /></div></Field>
          <Field label="Логотип" hint="PNG, SVG, JPEG или WEBP до 200 КБ, лучше квадратный"><div className="flex items-center gap-3">{b.logo && <img src={b.logo} alt="" className="h-10 w-10 object-contain" />}<input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" onChange={(e) => onLogo(e.target.files?.[0])} />{b.logo && <Button size="sm" variant="ghost" onClick={() => setB({ ...b, logo: null })}>Убрать</Button>}</div></Field>
          {err && <div className="text-sm text-red-600">{err}</div>}
          <ErrorText error={save.error} />
          <div className="flex gap-2">
            <Button disabled={save.isPending || !/^#[0-9a-fA-F]{6}$/.test(b.color)} onClick={() => save.mutate()}>Сохранить</Button>
            <Button variant="secondary" onClick={() => setB({ ...DEFAULT_BRANDING })}>Вернуть стандартный</Button>
          </div>
        </div>
        <div className="card space-y-3 p-4">
          <div className="text-sm font-medium">Предпросмотр</div>
          <div className="flex gap-1">{Object.entries(pal).map(([k, v]) => <div key={k} title={k} className="h-8 flex-1 rounded" style={{ background: `rgb(${v.join(',')})` }} />)}</div>
          <div className="flex items-center gap-2 rounded border border-slate-200 p-3"><img src={b.logo ?? '/favicon.svg'} alt="" className="h-8 w-8 object-contain" /><b>{b.name}</b></div>
          <button className="rounded-md px-3 py-1.5 text-sm text-white" style={{ background: b.color }}>Кнопка</button>
          <div className="text-xs text-slate-500">Сведения о программе (раздел «О программе») и правообладателе не меняются: они необходимы для учёта в реестре российского ПО.</div>
        </div>
      </div>
    </>
  );
}
